import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Check, X, Sparkles } from '../components/Icons.jsx';
import { Intro, Done, mainStyle, formatTime, difficultyPill } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';
import StuckPopIn from '../components/StuckPopIn.jsx';

const levels = {
  easy: { range: [2, 20], ops: ['+', '−', '×'], divMax: 10, label: 'Easy', sub: '2–20 · +, −, ×' },
  medium: { range: [10, 99], ops: ['+', '−', '×', '÷'], divMax: 12, label: 'Medium', sub: 'Two-digit · all 4 ops' },
  hard: { range: [50, 999], ops: ['+', '−', '×', '÷'], divMax: 15, label: 'Hard', sub: 'Three-digit · mixed' }
};

function makeQuestion(level) {
  const L = levels[level];
  const op = L.ops[Math.floor(Math.random() * L.ops.length)];
  const [lo, hi] = L.range;
  const r = () => lo + Math.floor(Math.random() * (hi - lo + 1));
  let a, b, answer;
  if (op === '+') { a = r(); b = r(); answer = a + b; }
  else if (op === '−') { a = r(); b = r(); if (b > a) [a, b] = [b, a]; answer = a - b; }
  else if (op === '×') {
    const mLo = level === 'easy' ? 2 : level === 'medium' ? 4 : 6;
    const mHi = level === 'easy' ? 12 : level === 'medium' ? 25 : 60;
    a = mLo + Math.floor(Math.random() * (mHi - mLo + 1));
    b = mLo + Math.floor(Math.random() * (mHi - mLo + 1));
    answer = a * b;
  } else {
    b = 2 + Math.floor(Math.random() * (L.divMax - 1));
    const factor = 2 + Math.floor(Math.random() * (Math.max(2, Math.floor(hi / b))));
    a = b * factor;
    answer = factor;
  }
  return { a, b, op, answer };
}

export default function MentalArithmetic() {
  const [stage, setStage] = useState('intro');
  const [level, setLevel] = useState('medium');
  const [ucatsen, setUcatsen] = useState(false);
  const [q, setQ] = useState(null);
  const [input, setInput] = useState('');
  const [score, setScore] = useState({ correct: 0, wrong: 0, attempts: 0 });
  const [wrongStreak, setWrongStreak] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [lastFlash, setLastFlash] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const duration = ucatsen ? 75 : 60;

  useEffect(() => {
    if (stage !== 'drill') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); setStage('done'); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  const start = () => {
    setScore({ correct: 0, wrong: 0, attempts: 0 });
    setSecondsLeft(duration);
    setInput('');
    setLastFlash(null);
    setQ(makeQuestion(level));
    setStage('drill');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const submit = () => {
    if (!q || input.trim() === '') return;
    const guess = Number(input);
    const ok = guess === q.answer;
    setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), wrong: s.wrong + (ok ? 0 : 1), attempts: s.attempts + 1 }));
    setWrongStreak((w) => ok ? 0 : w + 1);
    setLastFlash(ok ? 'ok' : 'no');
    setInput('');
    setQ(makeQuestion(level));
    setTimeout(() => setLastFlash(null), 250);
    inputRef.current?.focus();
  };

  const accuracy = score.attempts > 0 ? Math.round((score.correct / score.attempts) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 }}>
        <Link to="/practice" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          <ArrowLeft width="16" height="16" />Exit
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={{ fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}>Mental arithmetic</strong>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: secondsLeft < 10 ? '#b91c1c' : 'var(--text-2)', minWidth: 60, textAlign: 'right' }}>
          {stage === 'drill' && `${secondsLeft}s`}
        </div>
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="QR" title="Mental arithmetic"
          body="60-second sprint. No calculator. Mirrors the speed you need on the real QR section."
          how={['Pick a difficulty level', 'Solve as many as you can before the timer runs out', 'Press Enter to submit each answer']}
          difficulty={level} setDifficulty={setLevel}
          difficultyOptions={Object.entries(levels).map(([v, L]) => ({ v, label: L.label, sub: L.sub }))}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && <StuckPopIn wrongStreak={wrongStreak} slug="mental-arithmetic" sectionLabel="mental arithmetic" />}
      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
            <span>{levels[level].label} {ucatsen && '· UCATSEN'}</span>
            <span><strong style={{ color: 'var(--live-green)' }}>{score.correct}</strong> correct · <strong style={{ color: '#b91c1c' }}>{score.wrong}</strong> wrong</span>
          </div>

          <div style={{
            background: lastFlash === 'ok' ? '#e7f7ef' : lastFlash === 'no' ? '#fee2e2' : '#fff',
            border: `2px solid ${lastFlash === 'ok' ? 'var(--live-green)' : lastFlash === 'no' ? '#b91c1c' : 'var(--border)'}`,
            borderRadius: 16, padding: '40px 24px', textAlign: 'center', marginBottom: 18,
            transition: 'all 150ms ease'
          }}>
            <div style={{ fontSize: 'clamp(40px, 10vw, 64px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.02em', color: 'var(--brand-deep)' }}>
              {q.a} {q.op} {q.b} =
            </div>
          </div>

          <input
            ref={inputRef}
            type="number" inputMode="numeric"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Your answer"
            style={{
              width: '100%', padding: '18px 20px', fontSize: 26, fontWeight: 700, textAlign: 'center',
              border: '2px solid var(--border-strong)', borderRadius: 14,
              fontFamily: 'Montserrat, sans-serif'
            }}
          />

          <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={submit} className="btn btn-primary">Submit <Check width="14" height="14" /></button>
            <button onClick={() => setQ(makeQuestion(level))} className="btn btn-secondary">Skip <ArrowRight width="14" height="14" /></button>
          </div>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="QR" title="Mental arithmetic" stats={[
          { label: 'Correct', value: score.correct },
          { label: 'Wrong', value: score.wrong },
          { label: 'Accuracy', value: `${accuracy}%` }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug="mental-arithmetic" score={score.correct} accuracyPct={accuracy} metadata={{ level, ucatsen }} />
            {accuracy < 65 && score.attempts >= 5 && (
              <TutorNudge tutor={tutorFor('mental-arithmetic')} reason={`${accuracy}% accuracy at this speed leaks 4–8 marks on real QR.`} kicker="Your QR is leaking marks" />
            )}
          </>}
        />
      )}
    </div>
  );
}
